import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

function Liveeasy() {
  return (
    <>
      <Header />
      
      <section className="project-detail-section">
        <div className="container">
          <div className="project-detail-hero">
            <h1 className="project-detail-title">
              <span className="title-line-1">Liveasy –</span><br/>
              <span className="title-line-2">UI/UX Design Case Study</span>
            </h1>
            <p className="project-detail-subtitle">Designing brand identity, product touchpoints, and a data-heavy logistics platform</p>
            <div className="project-meta">
              <span>UI/UX Designer</span>
              <span>•</span>
              <span>Figma</span>
              <span>•</span>
              <span>Web & Mobile</span>
            </div>
          </div>

          <div className="project-images-single">
            <img src="/assets/liveasy-first.png" alt="Liveasy Overview" />
          </div>

          <div className="project-detail-content">
            <section className="content-section">
              <h2>Project Overview</h2>
              <p>Liveasy is an enterprise-level logistics and transportation management platform designed to streamline how high-volume shipments and transportation workflows are handled. Unlike consumer-facing apps, Liveasy operates in a high-stakes environment where data accuracy and operational speed are paramount.</p>
              
              <p>As a real-world client project, my role was to evolve the platform's visual identity and user interface across multiple touchpoints—from the core mobile experience to complex web-based administrative dashboards.</p>
            </section>

            <section className="content-section">
              <h2>Scope of Work</h2>
              <p>The project required a multi-disciplinary approach covering branding, mobile design, and web-scale dashboard architecture.</p>
              
              <h3>My specific responsibilities included:</h3>
              <ul>
                <li>Logo Design & Brand Identity</li>
                <li>App Update Screen (User retention & communication)</li>
                <li>WhatsApp Share Template (Logistics notification design)</li>
                <li>Website Homepage (UI design)</li>
                <li>Web Dashboard: My Loads Section</li>
                <li>Web Dashboard: Team Section</li>
              </ul>
            </section>

            <section className="content-section">
              <h2>The Challenge</h2>
              <p>Designing for logistics involves managing massive amounts of information without overwhelming the user. The primary challenge was to take 'dirty' data—long shipment IDs, multi-stop routes, and complex team hierarchies—and present it in an interface that feels clean and intuitive.</p>
              
              <p>We needed to create a look that was professionally enterprise-grade while ensuring that drivers, dispatchers, and business owners could perform their tasks without friction. Balancing high-density data tables with enough visual breathing room was the core puzzle.</p>
            </section>

            <section className="content-section">
              <h2>Design Goals</h2>
              <ul>
                <li>Data Readability: Prioritize information hierarchy so users find critical logistics data at a glance.</li>
                <li>Brand Trust: Create a logo and visual language that feels stable, reliable, and modern.</li>
                <li>Operational Consistency: Ensure a seamless experience between the mobile app, shareable templates, and the web dashboard.</li>
                <li>Scalability: Design UI components that can handle growth as the platform adds more features and team roles.</li>
              </ul>
            </section>

            <section className="content-section">
              <h2>Logo Design</h2>
              <p>The logo was designed to reflect the speed and reliability of the logistics sector. I focused on a geometric, upward-moving form to symbolize growth and movement. The mark was refined to ensure clarity at all scales, from a small favicon on a browser tab to a large-format physical print on a carrier vehicle.</p>
              
              <div className="project-images-single">
                <img src="/assets/liveasy-second.png" alt="Liveasy Logo Design" />
              </div>
            </section>

            <section className="content-section">
              <h2>App Update Screen</h2>
              <p>For the app update screen, the goal was minimal disruption. Logistics apps are critical tools; a forced update can be an inconvenience. I designed a clear, friendly prompt that explained the benefits of the update while providing clear 'Update' and 'Later' actions to respect the user's workflow.</p>
            </section>

            <section className="content-section">
              <h2>WhatsApp Share Template</h2>
              <p>In the logistics industry, WhatsApp is a primary communication channel. I designed a structured share template that organizes shipment details into a highly readable format. This ensures that even when data is viewed outside the app, it remains professional and easy to scan on small screens.</p>
            </section>

            <section className="content-section">
              <h2>Web Dashboard – My Loads Section</h2>
              <p>This is the heart of the dispatcher's workflow. I implemented a robust tabbed structure to manage active, pending, and completed loads. The table design focuses on high readability, using status chips for quick identification of shipment states and a logical column order that follows the standard logistics lifecycle.</p>
              
              <div className="two-images-centered">
                <img src="/assets/liveasy-third.png" alt="My Loads 1" />
                <img src="/assets/liveasy-fourth.png" alt="My Loads 2" />
              </div>
              
              <div className="vertical-large-images">
                <img src="/assets/liveasy-fifth.png" alt="My Loads 3" />
                <img src="/assets/liveasy-sixth.png" alt="My Loads 4" />
              </div>
            </section>

            <section className="content-section">
              <h2>Web Dashboard – Team Section</h2>
              <p>Managing people is as important as managing cargo. The Team section was designed for administrative clarity. I focused on clean forms for adding new members and a straightforward table for role visibility, ensuring admins can manage permissions without confusion.</p>
              
              <div className="large-single-image">
                <img src="/assets/liveasy-seventh.png" alt="Team Section" />
              </div>
            </section>

            <section className="content-section">
              <h2>Website Homepage Design</h2>
              <p>Working within a client-provided resolution, I designed a homepage that acts as the primary acquisition funnel. The hierarchy is built to state the value proposition immediately, followed by feature explanations and clear trust signals. The call-to-action strategy is prominent, guiding potential B2B partners toward a demo or sign-up.</p>
              
              <div className="large-single-image">
                <img src="/assets/liveasy-eight.png" alt="Homepage Design" />
              </div>
            </section>

            <section className="content-section">
              <h2>Visual Design System</h2>
              <p>A formal design system was developed to maintain coherence across the web and mobile platforms. I chose a professional color palette—anchored in deep blues and stable grays—to evoke trust. The typography was selected for its legibility in data-heavy tables, while the component library ensures that the design remains scalable for future development.</p>
              
              <div className="project-images-single">
                <img src="/assets/liveasy-tenth.png" alt="Visual Design System" />
              </div>
            </section>

            <section className="content-section">
              <h2>Final Outcome</h2>
              <p>The project resulted in a unified brand identity and a highly functional platform interface. By focusing on hierarchy and clarity, I transformed the client's complex requirements into a streamlined UX. The result is an enterprise-grade ecosystem that feels professional, trustworthy, and efficient.</p>
            </section>

            <section className="content-section">
              <h2>Interactive Prototype (Figma)</h2>
              
              <div className="project-links">
                <a href="https://www.figma.com/design/w91XWLcIuDz96uqFVTqV9E/liveeasy?node-id=0-1&t=RHjTr3sFDh3d9pwe-1" target="_blank" rel="noopener noreferrer" className="btn">
                  View Designs <span className="btn-arrow">→</span>
                </a>
              </div>
            </section>

            <section className="content-section">
              <h2>Learning & Reflection</h2>
              <p>Designing for Liveasy was a masterclass in handling complexity. I learned that in enterprise design, 'creative' takes a backseat to 'useful.' Every design decision had to be justified by its contribution to operational speed and data clarity.</p>
              
              <p>This project sharpened my ability to scale UI components for large datasets and reinforced the importance of consistent branding across diverse digital touchpoints.</p>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default Liveeasy;
